// Child naming with age-of-majority awareness.
//
// Parenting schedules and holiday arrangements only govern children who are
// still minors, and the age of majority differs by province: 18 in AB, MB,
// ON, PE, QC, SK; 19 in BC, NB, NL, NS, NT, NU, YT. Every piece of prose
// that names the children (agreement clauses, transition details, calendar
// names) routes through here so an aged-out child silently drops from the
// wording: one remaining minor is referred to by first name, two or more as
// "the Children" (defined term).
//
// Children are `{ name, dob }` where dob is 'YYYY-MM-DD' (what Divii passes).
// A child with no parseable dob is treated as a minor — wrongly including an
// adult child is a redundancy; wrongly excluding a minor drops them from a
// legal document.
//
// The two-or-more form is ALWAYS "the Children", never a list of names:
// that is the defined term the Agreement uses, and every generated sentence
// (clauses, transition details, calendar titles) must match it.

export const MAJORITY_AGE_BY_JURISDICTION = {
  AB: 18, MB: 18, ON: 18, PE: 18, QC: 18, SK: 18,
  BC: 19, NB: 19, NL: 19, NS: 19, NT: 19, NU: 19, YT: 19,
}

// Unknown jurisdiction defaults to 19 for the same fail-open reason as
// missing dobs: the higher threshold keeps more children in the schedule.
export function majorityAge(jurisdiction) {
  return MAJORITY_AGE_BY_JURISDICTION[jurisdiction] ?? 19
}

// Completed years as of `asOf` (calendar math, no timezone conversion).
// Returns null when dob is missing or unparseable.
export function childAge(dob, asOf = new Date()) {
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dob || ''))
  if (!m) return null
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])]
  let age = asOf.getFullYear() - y
  const birthdayReached =
    asOf.getMonth() + 1 > mo || (asOf.getMonth() + 1 === mo && asOf.getDate() >= d)
  if (!birthdayReached) age -= 1
  return age
}

function isMinor(child, jurisdiction, asOf) {
  const age = childAge(child?.dob, asOf)
  if (age == null) return true // unknown dob → assume minor (fail open)
  return age < majorityAge(jurisdiction)
}

export function minorChildren(children, jurisdiction, asOf = new Date()) {
  if (!Array.isArray(children)) return []
  return children.filter((c) => c && (c.name || '').trim() && isMinor(c, jurisdiction, asOf))
}

// The on-screen note naming the children an age threshold excluded. Lives here
// rather than in each builder because it states a legal conclusion (who this
// schedule does and does not govern) and the two copies must not drift.
export function agedOutNote(children, jurisdiction, asOf = new Date()) {
  const names = adultChildren(children, jurisdiction, asOf).map(firstNameOf).filter(Boolean)
  if (!names.length) return ''

  let list
  if (names.length === 1) {
    list = names[0]
  } else if (names.length === 2) {
    list = `${names[0]} and ${names[1]}`
  } else {
    list = `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
  }

  const singular = names.length === 1
  const verb = singular ? 'has' : 'have'
  const isAre = singular ? 'is' : 'are'
  return `${list} ${verb} reached the age of majority `
    + `(${majorityAge(jurisdiction)} in ${jurisdiction}) and ${isAre} not named in this schedule.`
}

// Children excluded by age — callers surface these in an on-screen note so
// the name change ("the Children" → "Mae") never looks like a glitch.
export function adultChildren(children, jurisdiction, asOf = new Date()) {
  if (!Array.isArray(children)) return []
  return children.filter((c) => c && (c.name || '').trim() && !isMinor(c, jurisdiction, asOf))
}

// "Mae Alleyne-Smith" → "Mae". Names Divii passes are usually already first
// names, in which case this is a no-op.
// "Mae Alleyne-Smith" -> "Alleyne-Smith". A single-token name is its own
// surname. Used for the download filename; see partyFileTitle in
// lib/agreement/filters.js for the convention it feeds.
export function lastNameOf(fullName) {
  const parts = String(fullName || '').trim().split(/\s+/).filter(Boolean)
  return parts.length ? parts[parts.length - 1] : ''
}

export function firstNameOf(child) {
  return ((child?.name || '').trim().split(/\s+/)[0]) || ''
}

// How generated wording refers to the children: a single minor by first
// name, two or more as the defined term "the Children", none → fallback.
export function childrenSubject(children, jurisdiction, opts = {}) {
  const { fallback = 'the children', asOf = new Date() } = opts
  const minors = minorChildren(children, jurisdiction, asOf)
  if (minors.length === 1) return firstNameOf(minors[0]) || 'the child'
  if (minors.length >= 2) return 'the Children'
  return fallback
}
